import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import { BulkMedicationsItem, BulkMedicationsRequest } from 'src/app/models/bulk-medications.model';
import { Medication, MedicationReminder, MedicationStatus } from 'src/app/models/fhir-models';
import {
  MedicationReview,
  MedicationReviewWritePayload,
  MedicationSnapshot,
} from 'src/app/models/medication-review.model';
import { FirestoreService } from 'src/app/services/firestore.service';
import { PatientService } from 'src/app/services/patient.service';
import { LoopDiureticConfigEntry } from './loop-diuretic-dose.util';
import { MedicationFrequencyOption, NihAutoComplete } from './fhir-medications.model';
import { normalizeMedicationHours } from './medication-hours.util';
import { environment } from 'src/environments/environment';

// Re-exported from the standalone util to keep existing import paths working while
// breaking the patient-detail.service <-> fhir-medications.service circular dependency.
export { normalizeMedicationHours } from './medication-hours.util';

@Injectable({
  providedIn: 'root',
})
export class FhirMedicationsService {
  private nihDisplayNamesApiurl = 'https://rxnav.nlm.nih.gov/REST/displaynames.json';
  constructor(
    private http: HttpClient,
    private fs: FirestoreService,
    private patientService: PatientService
  ) {}

  /**
   * Retrieve JSON from https://rxnav.nlm.nih.gov - Documentation https://lhncbc.nlm.nih.gov/RxNav/APIs/api-RxNorm.getDisplayTerms.html
   */
  getDisplayTerms(): Observable<NihAutoComplete> {
    return this.http.get<NihAutoComplete>(this.nihDisplayNamesApiurl);
  }

  bulkAddMedicationsToPatient(patientId: string, medications: BulkMedicationsItem[]): Observable<Medication[]> {
    const body: BulkMedicationsRequest = { medications };
    return this.http
      .post<
        ApiResponse<Medication[]>
      >(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/medications/bulk`, body)
      .pipe(map((res) => res.data));
  }

  updateMedicationForPatient(
    patientId: string,
    medicationId: string,
    medication: BulkMedicationsItem
  ): Observable<Medication> {
    return this.http
      .put<
        ApiResponse<Medication>
      >(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/medications/${medicationId}`, medication)
      .pipe(map((res) => res.data));
  }

  async removeMedicationFromPatient(patientId: string, medication: any): Promise<void> {
    const id = medication?.id && medication?.id?.length > 3 ? medication.id : medication.doc?.id;
    await this.removeMedicationReminder(patientId, medication.id);
    return this.fs.delete(`users/${patientId}/my_fhir_medications/${id}`);
  }

  async saveIndexDragAndDrop(patientId: string, medications: Medication[]): Promise<void> {
    await this.patientService.saveIndexDragAndDrop(patientId, {
      elements: medications,
      collectionName: 'my_fhir_medications',
    });
  }

  async getOptionsFrequencyMedications(): Promise<MedicationFrequencyOption[]> {
    return this.fs
      .doc<{ options: MedicationFrequencyOption[] }>('welby_configuration/frequency_medications')
      .get()
      .toPromise()
      .then((doc) => (doc.exists ? doc.data()?.options || [] : []))
      .catch((error) => {
        console.error('Error:', error);
        return [];
      });
  }

  /**
   * The loop diuretics in scope for BRD-CLIN-2026-006 §4.2's ceiling, and their ceilings.
   *
   * Mirrors `getOptionsFrequencyMedications` above, including its failure behaviour: an unreadable
   * document yields `[]`. That is deliberately the same as "the document does not exist yet", because
   * `resolveLoopDiureticDose` treats an empty config as "there is no ceiling to compare to" and asks
   * no question at all — it does NOT report every medication as unknown. Warning about every drug on
   * a failed read would be noise a nurse learns to dismiss.
   *
   * Seeded by welby-firestore's `SeedLoopDiuretics`, which is also the single source the Care Call
   * Guide's ceiling thresholds are derived from.
   */
  getLoopDiuretics$(): Observable<LoopDiureticConfigEntry[]> {
    return this.fs
      .doc<{ drugs: LoopDiureticConfigEntry[] }>('welby_configuration/loop_diuretics')
      .get()
      .pipe(
        map((doc) => (doc.exists ? doc.data()?.drugs || [] : [])),
        catchError(() => of([])),
      );
  }

  /**
   * An OBSERVABLE twin of `getOptionsFrequencyMedications`, which returns a promise.
   *
   * Both exist on purpose rather than one replacing the other. The promise form is what the detail
   * dialog already awaits, and rewriting its call sites is a change this feature has no business
   * making. The observable form is what the ceiling check needs: it composes into the `combineLatest`
   * over the realtime medication list, and a promise inside that stream leaves a microtask the
   * component's own tests can never settle — which is exactly how this was found.
   */
  getFrequencyOptions$(): Observable<MedicationFrequencyOption[]> {
    return this.fs
      .doc<{ options: MedicationFrequencyOption[] }>('welby_configuration/frequency_medications')
      .get()
      .pipe(
        map((doc) => (doc.exists ? doc.data()?.options || [] : [])),
        catchError(() => of([])),
      );
  }

  saveMedicationReminder(patientId: string, medicationReminder: MedicationReminder): Observable<MedicationReminder> {
    return this.http
      .post<
        ApiResponse<MedicationReminder>
      >(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/medications/save-reminder`, medicationReminder)
      .pipe(map((res) => res.data));
  }

  updateMedicationReminder(patientId: string, medicationReminder: MedicationReminder): Observable<MedicationReminder> {
    return this.http
      .post<
        ApiResponse<MedicationReminder>
      >(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/medications/update-reminder`, medicationReminder)
      .pipe(map((res) => res.data));
  }

  async removeMedicationReminder(patientId: string, medicationId: string): Promise<void> {
    const collection = this.fs.col(`medication_reminders`, (ref) =>
      ref.where('patientId', '==', patientId).where('medicationId', '==', medicationId)
    );
    const querySnapshot = await collection.get().toPromise();
    if (!querySnapshot.empty) {
      const docId = querySnapshot.docs[0].id;
      return this.fs.delete(`medication_reminders/${docId}`);
    }
  }

  async getMedicationReminder(patientId: string, medicationId: string): Promise<MedicationReminder> {
    return this.fs
      .col<MedicationReminder>(`medication_reminders`, (ref) =>
        ref.where('patientId', '==', patientId).where('medicationId', '==', medicationId)
      )
      .get()
      .toPromise()
      .then((querySnapshot) => {
        if (!querySnapshot.empty) {
          return querySnapshot.docs[0].data();
        }
        return null;
      })
      .catch((error) => {
        console.error('Error:', error);
        return null;
      });
  }

  /**
   * Submits a medication review to welby-micro-service. Goes through HTTP (not direct Firestore)
   * because the backend is the only authorized writer: it enforces reviewedBy === auth.uid and
   * performs the server-side staleness check that prevents concurrent reviewers from saving over
   * stale state. Firestore rules deny `create` from clients for `medication_reviews` to enforce
   * this invariant at the database boundary.
   *
   * Emits a single MedicationReview on success or errors with the HTTP response (typically a
   * 409 Conflict for stale baselines — handled by the component's handleSaveReviewError).
   *
   * Caveat: the `reviewedAt` field in the emitted value is JSON-serialized and arrives as a
   * plain object `{_seconds, _nanoseconds}`, NOT a real `firebase.firestore.Timestamp`. The
   * footer that renders the timestamp reads from {@link getLastMedicationReview$} (a direct
   * Firestore subscription) instead, where AngularFire rehydrates it to a proper Timestamp.
   */
  saveMedicationReview(patientId: string, review: MedicationReviewWritePayload): Observable<MedicationReview> {
    return this.http
      .post<
        ApiResponse<MedicationReview>
      >(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/medications/reviews`, review)
      .pipe(map((res: ApiResponse<MedicationReview>) => res.data));
  }

  /**
   * Realtime stream of the patient's most recent medication review (or null when the
   * subcollection is empty / not yet readable). Subscribes directly to Firestore — not via
   * the backend — so the footer updates instantly when a new review is written from any
   * source (this client, another tab, another nurse) without polling.
   *
   * The orderBy + limit(1) keeps reads cheap (1 doc per emission). Firestore returns proper
   * `Timestamp` instances here (unlike the HTTP response from {@link saveMedicationReview}),
   * so `reviewedAt.toDate()` works in the template.
   *
   * Cleanup: this Observable is consumed only via `async` pipe in the template plus the
   * combineLatest inside `canReview$`. The component wraps it with
   * `shareReplay({ bufferSize: 1, refCount: true })` so the underlying Firestore listener
   * closes automatically when both consumers detach (component destroy or patient switch).
   */
  getLastMedicationReview$(patientId: string): Observable<MedicationReview | null> {
    return this.fs
      .colWithIds$<MedicationReview>(`users/${patientId}/medication_reviews`, (ref: firebase.firestore.Query) =>
        ref.orderBy('reviewedAt', 'desc').limit(1)
      )
      .pipe(map((reviews: MedicationReview[]) => (reviews.length ? reviews[0] : null)));
  }

  /**
   * Projects the Medication list to the immutable visible-field subset that gets persisted
   * inline in the review document. Drops Firestore-internal fields (`doc`, `index`, etc.) and
   * non-visible clinical fields (`prescriber`, `rx_id`, etc.) — keeps only what the reviewer
   * actually saw on screen. Filters out meds without an id defensively (shouldn't happen with
   * clean data) and supplies safe defaults so the stored audit trail is never `undefined`.
   */
  buildMedicationSnapshot(meds: Medication[]): MedicationSnapshot[] {
    return (meds ?? [])
      .filter((m: Medication) => !!m?.id)
      .map((m: Medication) => ({
        id: m.id,
        name: m.name ?? '',
        dosage: m.dosage ?? '',
        frequency: m.frequency ?? 'N/A',
        hours: normalizeMedicationHours(m.hours),
        status: m.status ?? MedicationStatus.ACTIVE,
      }));
  }

  /**
   * Returns the max `updated_at` / `updatedAt` (epoch ms) across the given medications.
   * Returns 0 when none of the meds have a timestamp (legacy data). Used as the baseline
   * sent to the backend so it can detect stale reviews and reject them with 409.
   */
  computeMedicationsBaselineAt(meds: Medication[]): number {
    return (meds ?? []).reduce((max: number, m: Medication) => {
      const withTs = m as Medication & {
        updated_at?: { toMillis?: () => number };
        updatedAt?: { toMillis?: () => number };
      };
      const ms = withTs.updated_at?.toMillis?.() ?? withTs.updatedAt?.toMillis?.() ?? 0;
      return ms > max ? ms : max;
    }, 0);
  }
}
